/**
 * Static reference data from the Bayan Integration Guide for Freight
 * Forwarders (V7, 07/10/2025) appendices.
 *
 * These IDs are assigned by ELM and are stable — they are safe to commit.
 * The ONE lookup that is fetched live is good types (appendix is huge and
 * ELM adds children over time); see `BAYAN_GOOD_TYPE_FALLBACK` for the
 * offline map used when the lookup endpoint is unreachable.
 *
 * Do NOT renumber anything here. If ELM deprecates a value, keep the entry
 * and mark it — waybills already issued reference the old id.
 */

/** Appendix 6.1 — plate type id. */
export const BAYAN_PLATE_TYPES = {
  public_transport: 2,
  private_transport: 3,
  heavy_equipment: 7,
  export: 8,
  temporary: 11,
} as const;
export type BayanPlateTypeKey = keyof typeof BAYAN_PLATE_TYPES;
export type BayanPlateTypeId = (typeof BAYAN_PLATE_TYPES)[BayanPlateTypeKey];

/** Appendix 6.5 — trip / waybill status id. */
export const BAYAN_STATUSES = {
  1: 'active',
  2: 'canceled',
  3: 'closed',
  4: 'finished',
  5: 'not_delivered',
  6: 'multi_status',
} as const;
export type BayanStatusId = keyof typeof BAYAN_STATUSES;

/** Appendix 6.6 — payment method id. */
export const BAYAN_PAYMENT_METHODS = {
  cash: 1,
  atm_card: 2,
  credit_card: 3,
  sdad: 4,
  check: 5,
  bank_transfer: 6,
} as const;
export type BayanPaymentMethodKey = keyof typeof BAYAN_PAYMENT_METHODS;

/** Default payment method when the Org has not configured one. */
export const BAYAN_DEFAULT_PAYMENT_METHOD_ID = BAYAN_PAYMENT_METHODS.cash;

/** Appendix 6.7 — weight unit id (`unitId`). V9 splits weight vs packaging. */
export const BAYAN_ITEM_UNITS = {
  /** @deprecated V9 weight units are only Ton / KG. Kept for legacy payloads. */
  each: 1,
  ton: 2,
  /** @deprecated Prefer {@link BAYAN_PACKAGING_UNITS.pallet}. */
  pallet: 3,
  /** @deprecated Prefer {@link BAYAN_PACKAGING_UNITS.box}. */
  box: 4,
  kg: 5,
  /** @deprecated Prefer {@link BAYAN_PACKAGING_UNITS.case}. */
  case: 6,
  /** @deprecated Prefer {@link BAYAN_PACKAGING_UNITS.container} on packagingUnitId. */
  container: 7,
} as const;
export type BayanItemUnitKey = keyof typeof BAYAN_ITEM_UNITS;
export type BayanItemUnitId = (typeof BAYAN_ITEM_UNITS)[BayanItemUnitKey];

/**
 * Appendix 6.7 — packaging unit id (`packagingUnitId`).
 *
 * When set, Bayan requires `dimensions` on the item (guide § 5.1).
 * Container (حاوية) is a packaging unit, not a weight unit — sending
 * `unitId: 7` alone produces `[dimensions is required]` (observed Sep 2026).
 */
export const BAYAN_PACKAGING_UNITS = {
  each: 1,
  pallet: 3,
  box: 4,
  case: 6,
  container: 7,
} as const;
export type BayanPackagingUnitKey = keyof typeof BAYAN_PACKAGING_UNITS;
export type BayanPackagingUnitId = (typeof BAYAN_PACKAGING_UNITS)[BayanPackagingUnitKey];

/**
 * Units that force `weight` to be mandatory on an item regardless of
 * whether `dimensions` is set (guide § 5.1 field spec).
 */
export const BAYAN_WEIGHT_REQUIRED_UNIT_IDS: readonly BayanItemUnitId[] = [
  BAYAN_ITEM_UNITS.ton,
  BAYAN_ITEM_UNITS.kg,
];

/** Appendix 6.8 — cancel status reason id. */
export const BAYAN_CANCEL_REASONS = {
  waybill_cancellation: 1,
  recipient_refused: 2,
  goods_damaged: 3,
  theft: 4,
} as const;
export type BayanCancelReasonKey = keyof typeof BAYAN_CANCEL_REASONS;

/** Default cancel reason — plain "waybill cancellation". */
export const BAYAN_DEFAULT_CANCEL_REASON_ID = BAYAN_CANCEL_REASONS.waybill_cancellation;

/**
 * Good-type parents that trigger conditional field requirements
 * (guide § 5.1 field spec).
 *
 * - `dangerous_materials` (35) → item MUST carry `dangerousCode`
 * - `vehicle_transportation` (13) → item MUST carry `chassisNumber` OR
 *   `sequenceNumber`
 */
export const BAYAN_GOOD_TYPE_PARENTS = {
  food_items: 12,
  vehicle_transportation: 13,
  petroleum_products: 10,
  building_and_construction: 34,
  dangerous_materials: 35,
} as const;

/** Carrier entity type on the create-trip payload. */
export const BAYAN_CARRIER_TYPES = ['COMPANY', 'INDIVIDUAL'] as const;
export type BayanCarrierType = (typeof BAYAN_CARRIER_TYPES)[number];

/** Saudi Arabia country code — the only value v1 issues waybills for. */
export const BAYAN_COUNTRY_CODE_SA = 'SA';
